<app-tab-toolbar [tabId]="'accounting'" [selectedYear]="selectedYear" [saveDisabled]="(!hasChanges && !isCarriedForward) || isFinancialsIndustry" [showOtherMeasurement]="true" [otherMeasurement]="otherMeasurement" [showKeywordsToggle]="true" [keywordsVisible]="showKeywords" (keywordsVisibleChange)="showKeywords = $event" (yearChange)="onYearChange($event)" (otherMeasurementChange)="onOtherMeasurementChange($event)" (save)="onSave()">
  @if (showYearWarning) {
    <div class="year-warning">
      <mat-icon>warning</mat-icon>
      @if (isCarriedForward) {
        <span>There is no data for the selected year. Some fields are shown from a previous year.</span>
      } @else {
        <span>There is no data for the selected year</span>
      }
    </div>
  }
  @if (isFinancialsIndustry) {
    <div class="year-warning">
      <mat-icon>info</mat-icon>
      <span>This company is in the Financials industry for this measurement — accounting fields are disabled.</span>
    </div>
  }
</app-tab-toolbar>

<div class="accounting-content" [formGroup]="form" [appReadOnlyTab]="'accounting'">

  <!-- Currency & Amounts Expressed In -->
  <section class="section-container nrg-border-radius-second-level">
    <div class="top-fields">
      <div class="top-field">
        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Currency</mat-label>
          <mat-select formControlName="currencyId">
            <mat-option value="">(None)</mat-option>
            @for (currency of currencies; track currency.id) {
              <mat-option [value]="currency.id">{{ currency.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (otherMeasurement && otherData?.currencyId != null && otherData!.currencyId !== form.get('currencyId')!.value) {
          @let otherCurrency = getOtherCurrencyName();
          @if (otherCurrency) { <div class="comparison-hint">{{ otherCurrency }}</div> }
        }
      </div>
      <div class="top-field top-field-amounts">
        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Amounts Expressed In</mat-label>
          <mat-select formControlName="amountsExpressedIn">
            <mat-option [value]="null">(None)</mat-option>
            @for (option of amountsExpressedInOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (otherMeasurement && otherData?.amountsExpressedIn != null && otherData!.amountsExpressedIn !== form.get('amountsExpressedIn')!.value) {
          @let otherAmounts = getOtherAmountsExpressedIn();
          @if (otherAmounts) { <div class="comparison-hint">{{ otherAmounts }}</div> }
        }
      </div>
    </div>
  </section>

  <div class="columns-wrapper">

    <!-- LEFT COLUMN — Income Statement -->
    <div class="column">
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Income Statement</span>
        </div>
        <div class="accounting-grid" [class.no-keywords]="!showKeywords">
          <div class="grid-label grid-col-header">Variable</div>
          @if (showKeywords) { <div class="grid-col-header grid-keyword-header">Keyword</div> }
          <div class="grid-col-header grid-value-field">Amount</div>
          <div class="grid-col-header grid-value-field">Previous</div>

          @for (field of incomeStatementFields; track field.key) {
            <div class="field-row">
              <div class="grid-label">{{ field.label }}</div>
              @if (showKeywords) {
                <div class="grid-field grid-keyword-field" formGroupName="keywords">
                  <app-keyword-input [name]="field.keywordKey" />
                </div>
              }
              <div class="grid-field grid-value-field">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
                @if (otherMeasurement && getOtherValue(field.key) != null) {
                  <div class="comparison-hint comparison-hint-value">{{ formatNumber(getOtherValue(field.key)) }}</div>
                }
              </div>
              <div class="grid-field grid-value-field" formGroupName="previous">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
              </div>
            </div>
          }
        </div>
      </section>
    </div>

    <!-- RIGHT COLUMN — Cash Flow, Current Assets, Liabilities -->
    <div class="column">

      <!-- CASH FLOW STATEMENT -->
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Cash Flow Statement</span>
        </div>
        <div class="accounting-grid" [class.no-keywords]="!showKeywords">
          <div class="grid-label grid-col-header">Variable</div>
          @if (showKeywords) { <div class="grid-col-header grid-keyword-header">Keyword</div> }
          <div class="grid-col-header grid-value-field">Amount</div>
          <div class="grid-col-header grid-value-field">Previous</div>

          @for (field of cashFlowFields; track field.key) {
            <div class="field-row">
              <div class="grid-label">{{ field.label }}</div>
              @if (showKeywords) {
                <div class="grid-field grid-keyword-field" formGroupName="keywords">
                  <app-keyword-input [name]="field.keywordKey" />
                </div>
              }
              <div class="grid-field grid-value-field">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
                @if (otherMeasurement && getOtherValue(field.key) != null) {
                  <div class="comparison-hint comparison-hint-value">{{ formatNumber(getOtherValue(field.key)) }}</div>
                }
              </div>
              <div class="grid-field grid-value-field" formGroupName="previous">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
              </div>
            </div>
          }
        </div>
      </section>

      <!-- CURRENT ASSETS -->
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Current Assets</span>
        </div>
        <div class="accounting-grid" [class.no-keywords]="!showKeywords">
          <div class="grid-label grid-col-header">Variable</div>
          @if (showKeywords) { <div class="grid-col-header grid-keyword-header">Keyword</div> }
          <div class="grid-col-header grid-value-field">Amount</div>
          <div class="grid-col-header grid-value-field">Previous</div>

          @for (field of currentAssetsFields; track field.key) {
            <div class="field-row">
              <div class="grid-label">{{ field.label }}</div>
              @if (showKeywords) {
                <div class="grid-field grid-keyword-field" formGroupName="keywords">
                  <app-keyword-input [name]="field.keywordKey" />
                </div>
              }
              <div class="grid-field grid-value-field">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
                @if (otherMeasurement && getOtherValue(field.key) != null) {
                  <div class="comparison-hint comparison-hint-value">{{ formatNumber(getOtherValue(field.key)) }}</div>
                }
              </div>
              <div class="grid-field grid-value-field" formGroupName="previous">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
              </div>
            </div>
          }
        </div>
      </section>

      <!-- LIABILITIES -->
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Liabilities</span>
        </div>
        <div class="accounting-grid" [class.no-keywords]="!showKeywords">
          <div class="grid-label grid-col-header">Variable</div>
          @if (showKeywords) { <div class="grid-col-header grid-keyword-header">Keyword</div> }
          <div class="grid-col-header grid-value-field">Amount</div>
          <div class="grid-col-header grid-value-field">Previous</div>

          @for (field of liabilitiesFields; track field.key) {
            <div class="field-row">
              <div class="grid-label">{{ field.label }}</div>
              @if (showKeywords) {
                <div class="grid-field grid-keyword-field" formGroupName="keywords">
                  <app-keyword-input [name]="field.keywordKey" />
                </div>
              }
              <div class="grid-field grid-value-field">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
                @if (otherMeasurement && getOtherValue(field.key) != null) {
                  <div class="comparison-hint comparison-hint-value">{{ formatNumber(getOtherValue(field.key)) }}</div>
                }
              </div>
              <div class="grid-field grid-value-field" formGroupName="previous">
                <mat-form-field appearance="fill" class="form-field">
                  <input matInput type="text" inputmode="numeric" appNumberFormat [formControlName]="field.key" />
                </mat-form-field>
              </div>
            </div>
          }
        </div>
      </section>

    </div>
  </div>

</div>

<ngx-spinner name="primary" type="ball-spin-clockwise" [fullScreen]="false" size="medium" />
